/**
 * `useOrgSubscription` — live read of the signed-in user's primary
 * org + its subscription state.
 *
 * Reads from:
 *   - `users/{uid}` to get `primaryOrgId`
 *   - `organizations/{orgId}` to get `name` + `subscription`
 *
 * Both are Firestore snapshots — when the org's subscription tier
 * changes (e.g. webhook fires after a Razorpay payment), this hook
 * re-renders within ~1 second. That's how the Plans page flips a
 * tier card from "Upgrade" to "Current" right after payment.
 */
import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect, useState } from 'react';

import { db } from '@/lib/firebase';
import { useAuth } from '@/auth/AuthProvider';

export type OrgSubscription = {
  tier?: string; // 'free' | 'solo' | 'studio' | 'studio_plus' | 'agency'
  status?: string; // 'active' | 'past_due' | 'cancelled' | 'expired'
  expiresAt?: { toMillis(): number } | null;
  willRenew?: boolean;
  /** What wrote this state: 'webhook' = App Store / Google Play (via
   *  RevenueCat), 'razorpay' = legacy web billing, 'manual' = admin. */
  source?: string;
  productId?: string | null;
  period?: 'monthly' | 'annual' | null;
  /** The active Razorpay subscription id (`sub_xxx`) — written by
   *  the webhook on subscription.activated. Distinguishes "customer
   *  has a real Razorpay sub we can update" from "customer is on
   *  past_due / cancelled". Billing.tsx uses it to offer Cancel on
   *  legacy web subscriptions. */
  razorpaySubscriptionId?: string | null;
  /** When the customer has queued a plan change via Razorpay's
   *  Subscription Update API (downgrade or scheduled-upgrade), the
   *  webhook stamps this. UI uses it to show "Downgrading to Solo
   *  on YYYY-MM-DD" + offer a "Cancel scheduled change" link. */
  scheduledChange?: {
    /** Upcoming tier the subscription will switch to. */
    tier?: string;
    /** Effective date — exactly `subscription.current_end`. */
    effectiveAt?: { toMillis(): number } | null;
    /** Razorpay plan id we'll switch to (audit only). */
    planId?: string | null;
  } | null;
};

export type OrgSummary = {
  /** Firestore org id. */
  id: string;
  /** Studio display name. */
  name: string;
  /** Subscription block from `organizations/{orgId}.subscription`. */
  subscription: OrgSubscription | null;
};

export type UseOrgSubscriptionResult = {
  /** The org the signed-in user is associated with (their primary
   *  org). Null when the user has no org (just signed up, never
   *  joined). */
  org: OrgSummary | null;
  /** True until the first snapshot resolves. */
  loading: boolean;
  /** Error message if the read failed (e.g. permission denied
   *  because the user isn't in the org's roles map). */
  error: string | null;
};

export function useOrgSubscription(): UseOrgSubscriptionResult {
  const { user, loading: authLoading } = useAuth();
  const [org, setOrg] = useState<OrgSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setOrg(null);
      setLoading(false);
      return;
    }

    let orgUnsub: (() => void) | null = null;

    const userUnsub = onSnapshot(
      doc(db, 'users', user.uid),
      (userSnap) => {
        const userData = userSnap.data();
        const primaryOrgId =
          typeof userData?.primaryOrgId === 'string'
            ? userData.primaryOrgId
            : null;
        if (!primaryOrgId) {
          setOrg(null);
          setLoading(false);
          return;
        }
        // Tear down the previous org listener if the user switched
        // primary org (rare but possible).
        if (orgUnsub) orgUnsub();
        orgUnsub = onSnapshot(
          doc(db, 'organizations', primaryOrgId),
          (orgSnap) => {
            const data = orgSnap.data();
            if (!data) {
              setOrg(null);
            } else {
              setOrg({
                id: primaryOrgId,
                name: (data.name as string) || 'Your studio',
                subscription:
                  (data.subscription as OrgSubscription) ?? null,
              });
            }
            setLoading(false);
          },
          (err) => {
            setError(err.message);
            setLoading(false);
          },
        );
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      },
    );

    return () => {
      userUnsub();
      if (orgUnsub) orgUnsub();
    };
  }, [user, authLoading]);

  return { org, loading, error };
}
